import {
    Box,
    Typography,
    useTheme,
    CircularProgress
} from '@mui/material';
import React, { useEffect } from 'react';
import { BarChart } from '@mui/x-charts/BarChart';
import { Moment } from 'moment';
import PortalCard from '../../../../../components/PortalCard';
import { productAnalyticsStore } from '../../../../../app/stores/ProductAnalytics';
import { SalesVsNonsalesData } from '../../../../../models/Analytics';

interface SalesVsNonsalesChartProps {
    dateRange?: [Moment, Moment];
}

const SalesVsNonsalesChart: React.FC<SalesVsNonsalesChartProps> = ({ dateRange }) => {
    const theme = useTheme();

    const {
        salesVsNonsales,
        salesVsNonsalesLoading,
        salesVsNonsalesError,
        fetchSalesVsNonsales
    } = productAnalyticsStore((state) => ({
        salesVsNonsales: state.salesVsNonsales,
        salesVsNonsalesLoading: state.salesVsNonsalesLoading,
        salesVsNonsalesError: state.salesVsNonsalesError,
        fetchSalesVsNonsales: state.fetchSalesVsNonsales
    }));

    useEffect(() => {
        const startDate = dateRange?.[0]?.toISOString();
        const endDate = dateRange?.[1]?.toISOString();
        fetchSalesVsNonsales(startDate, endDate);
    }, [dateRange, fetchSalesVsNonsales]);

    const chartData = salesVsNonsales;
    const loading = salesVsNonsalesLoading;
    const error = salesVsNonsalesError;

    const colors = [theme.palette.success.main, theme.palette.info.main];
    
    // Prepare data for horizontal bar chart
    const labels = chartData.map(item => item.priceType);
    const values = chartData.map(item => item.totalViews);
    const chartColors = chartData.map((item, index) => 
        item.priceType === 'On Sale' ? colors[0] : colors[1]
    );

    // Calculate total views for percentage calculation
    const totalViews = chartData.reduce((sum, item) => sum + item.totalViews, 0);

    return (
        <PortalCard
            sx={{
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Box sx={{ mb: 2 }}>
                <Typography variant='h6' fontFamily='primaryFont' fontWeight='bold' sx={{ mb: 0.5 }}>
                    Sales vs Non-Sales Views
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                    Product views comparison by pricing type
                </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, minHeight: 200, width: '100%', position: 'relative' }}>
                {loading ? (
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography variant='body2' color='error'>
                            {error}
                        </Typography>
                    </Box>
                ) : chartData.length > 0 ? (
                    <>
                        <BarChart
                            xAxis={[{
                                data: labels,
                                scaleType: 'band',
                                tickLabelStyle: {
                                    fontSize: '0.875rem',
                                    fill: theme.palette.text.primary,
                                    fontWeight: 500
                                }
                            }]}
                            series={[{
                                data: values,
                                label: 'Views',
                            }]}
                            colors={chartColors}
                            height={300}
                            margin={{ top: 20, right: 30, bottom: 60, left: 60 }}
                            layout='vertical'
                            slotProps={{
                                legend: { hidden: true }
                            }}
                            sx={{
                                '& .MuiChartsAxis-tickLabel': {
                                    fontSize: '0.875rem',
                                    fill: theme.palette.text.secondary,
                                },
                                '& .MuiChartsAxis-line': {
                                    stroke: theme.palette.divider,
                                },
                                '& .MuiBarElement-root': {
                                    rx: 4,
                                },
                            }}
                        />
                        {/* Stats below chart */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 3, 
                            justifyContent: 'center', 
                            mt: 2,
                            flexWrap: 'wrap'
                        }}>
                            {chartData.map((item, index) => {
                                const percentage = totalViews > 0 ? Math.round((item.totalViews / totalViews) * 100) : 0;
                                const color = colors[index];
                                
                                return (
                                    <Box
                                        key={item.priceType}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box
                                                sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    backgroundColor: color,
                                                    flexShrink: 0
                                                }}
                                            />
                                            <Typography variant='body2' fontWeight={600} color='text.secondary'>
                                                {item.priceType}
                                            </Typography>
                                        </Box>
                                        <Typography variant='h5' fontWeight='bold' color={color}>
                                            {item.totalViews}
                                        </Typography>
                                        <Typography variant='caption' color='text.secondary'>
                                            {item.productCount} products • {percentage}%
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </>
                ) : (
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography variant='body2' color='text.secondary'>
                            No data available
                        </Typography>
                    </Box>
                )}
            </Box>
        </PortalCard>
    );
};

export default SalesVsNonsalesChart;

